import clsx from 'clsx'
import { CheckCircle, Loader2, Shield, XCircle } from 'lucide-react'
import { useEffect } from 'react'

import useDingtalkStore from '@/pages/auth/stores/dingtalkStore'

const FreeLogin: React.FC = () => {
  const { 
    doLogin, 
    envStatus, 
    codeStatus, 
    loginStatus, 
    error
  } = useDingtalkStore()

  // 组件加载时自动触发登录，但只执行一次
  useEffect(() => {
    console.info('免登录认证开始...')
    doLogin()
  }, [doLogin])

  return (
    <>
      <div className={clsx('flex', 'flex-col', 'items-center', 'justify-center', 'p-8', 'space-y-6')}>
        {/* 认证状态 */}
        <div className={clsx('relative')}>
          <div className={clsx(
            'w-16', 'h-16', 'rounded-full', 'flex', 'items-center', 'justify-center',
            error ? 'bg-red-100' : loginStatus === 'success' ? 'bg-green-100' : 'bg-blue-100'
          )}>
            <Shield className={clsx(
              'w-8', 'h-8',
              error ? 'text-red-600' : loginStatus === 'success' ? 'text-green-600' : 'text-blue-600'
            )} />
          </div>
          <div className={clsx(
            'absolute', '-bottom-1', '-right-1', 'w-6', 'h-6', 'bg-white', 'rounded-full', 
            'flex', 'items-center', 'justify-center', 'border-2',
            error ? 'border-red-100' : loginStatus === 'success' ? 'border-green-100' : 'border-blue-100'
          )}>
            {error ? (
              <XCircle className={clsx('w-4', 'h-4', 'text-red-600')} />
            ) : loginStatus === 'success' ? (
              <CheckCircle className={clsx('w-4', 'h-4', 'text-green-600')} />
            ) : (
              <Loader2 className={clsx('w-4', 'h-4', 'text-blue-600', 'animate-spin')} />
            )}
          </div>
        </div>

        <div className={clsx('text-center', 'space-y-2')}>
          <h3 className={clsx('text-lg', 'font-semibold', 'text-gray-800')}>
            {error ? '身份认证失败' : loginStatus === 'success' ? '身份认证成功' : '企业身份认证中'}
          </h3>
          <p className={clsx('text-sm', error ? 'text-red-600' : 'text-gray-600')}>
            {error || (loginStatus === 'success' ? '已成功验证您的钉钉企业身份' : '正在验证您的钉钉企业身份信息...')}
          </p>
        </div>

        {/* 认证步骤指示器 */}
        <div className={clsx('space-y-3', 'w-full', 'max-w-sm')}>
          {/* 检测钉钉环境 */}
          <div className={clsx('flex', 'items-center', 'text-sm')}>
            <div className={clsx(
              'w-4', 'h-4', 'rounded-full', 'flex', 'items-center', 'justify-center', 'mr-3',
              envStatus === 'success' ? 'bg-green-600' : 
              envStatus === 'failure' ? 'bg-red-600' :
              envStatus === 'loading' ? 'bg-blue-600' : 'bg-gray-300'
            )}>
              {envStatus === 'success' ? (
                <CheckCircle className={clsx('w-3', 'h-3', 'text-white')} />
              ) : envStatus === 'failure' ? (
                <XCircle className={clsx('w-3', 'h-3', 'text-white')} />
              ) : envStatus === 'loading' ? (
                <Loader2 className={clsx('w-3', 'h-3', 'text-white', 'animate-spin')} />
              ) : null}
            </div>
            <span className={clsx(
              envStatus === 'success' ? 'text-green-700' : 
              envStatus === 'failure' ? 'text-red-700' :
              envStatus === 'loading' ? 'text-blue-700' : 'text-gray-400'
            )}>检测钉钉环境</span>
          </div>

          {/* 获取授权码 */}
          <div className={clsx('flex', 'items-center', 'text-sm')}>
            <div className={clsx(
              'w-4', 'h-4', 'rounded-full', 'flex', 'items-center', 'justify-center', 'mr-3',
              codeStatus === 'success' ? 'bg-green-600' : 
              codeStatus === 'failure' ? 'bg-red-600' :
              codeStatus === 'loading' ? 'bg-blue-600' : 'bg-gray-300'
            )}>
              {codeStatus === 'success' ? (
                <CheckCircle className={clsx('w-3', 'h-3', 'text-white')} />
              ) : codeStatus === 'failure' ? (
                <XCircle className={clsx('w-3', 'h-3', 'text-white')} />
              ) : codeStatus === 'loading' ? (
                <Loader2 className={clsx('w-3', 'h-3', 'text-white', 'animate-spin')} />
              ) : null}
            </div>
            <span className={clsx(
              codeStatus === 'success' ? 'text-green-700' : 
              codeStatus === 'failure' ? 'text-red-700' :
              codeStatus === 'loading' ? 'text-blue-700' : 'text-gray-400'
            )}>获取授权码</span>
          </div>

          {/* 验证用户身份 */}
          <div className={clsx('flex', 'items-center', 'text-sm')}>
            <div className={clsx(
              'w-4', 'h-4', 'rounded-full', 'flex', 'items-center', 'justify-center', 'mr-3',
              loginStatus === 'success' ? 'bg-green-600' : 
              loginStatus === 'failure' ? 'bg-red-600' :
              loginStatus === 'loading' ? 'bg-blue-600' : 'bg-gray-300'
            )}>
              {loginStatus === 'success' ? (
                <CheckCircle className={clsx('w-3', 'h-3', 'text-white')} />
              ) : loginStatus === 'failure' ? (
                <XCircle className={clsx('w-3', 'h-3', 'text-white')} />
              ) : loginStatus === 'loading' ? (
                <Loader2 className={clsx('w-3', 'h-3', 'text-white', 'animate-spin')} />
              ) : null}
            </div>
            <span className={clsx(
              loginStatus === 'success' ? 'text-green-700' : 
              loginStatus === 'failure' ? 'text-red-700' :
              loginStatus === 'loading' ? 'text-blue-700' : 'text-gray-400'
            )}>验证用户身份</span>
          </div>
        </div>
      </div>
    </>
  )
}

export default FreeLogin
